import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  KeyboardArrowDown as ArrowDownIcon,
  AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserAccess } from '../../context/UserAccessContext';
import { useAuth0Token } from '../../hooks/useAuth0Token';
import { getCachedAdvisorConfigs } from '../../services/advisorConfigService';
import NewPlanRequestModal from './components/NewPlanRequestModal';
import PreferencesModal from './components/PreferencesModal';
import { tokens } from '../../theme/tokens';
import planscoutLogo from '../../assets/logos/planscout-logo.png';

interface NavItem {
  text: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { text: 'Opportunities', path: '/dashboard/opportunities' },
  { text: 'Client & Plans', path: '/dashboard/my-clients' },
  { text: 'Plan Engagement', path: '/dashboard/engagement' },
  // Resources is unlisted until the library is ready:
  // { text: 'Resources', path: '/dashboard/resources' },
];

// Desktop surfaces these on the Client & Plans page itself (Add Client / My Plans
// buttons); the mobile menu still lists them since there's no room for buttons.
const MORE_ITEMS: NavItem[] = [{ text: 'My Plans', path: '/dashboard/my-plans' }];

const TopNavLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth0();
  const { permissions, user: dbUser, isAdmin, role, customerIds, viewAsCustomerId, viewAsAdvisorName, exitViewAs } =
    useUserAccess();
  const { token } = useAuth0Token();

  const canManageUsers = permissions?.canManageUsers || false;

  // Advisor headshot for the user-menu avatar: match the logged-in user's email
  // against their accessible advisor configs, falling back to the first assigned
  // customerId with a photo. No match → initial-letter avatar.
  const [advisorPhotoUrl, setAdvisorPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getCachedAdvisorConfigs(token)
      .then((configs) => {
        if (cancelled) return;
        const email = (user?.email || '').toLowerCase();
        const byEmail = configs.find(
          (c) => c.advisorPhotoUrl && (c.email || '').toLowerCase() === email
        );
        const byAssigned = configs.find(
          (c) => c.advisorPhotoUrl && customerIds.includes(c.customerId)
        );
        setAdvisorPhotoUrl(byEmail?.advisorPhotoUrl || byAssigned?.advisorPhotoUrl || null);
      })
      .catch(() => {
        // Config lookup is cosmetic here — keep the letter avatar.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.email, customerIds.join(',')]);

  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [adminAnchor, setAdminAnchor] = useState<null | HTMLElement>(null);
  const [mobileAnchor, setMobileAnchor] = useState<null | HTMLElement>(null);
  const [newPlanModalOpen, setNewPlanModalOpen] = useState(false);
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);

  const adminItems: NavItem[] = canManageUsers
    ? [
        { text: 'Customer Management', path: '/dashboard/customer-management' },
        { text: 'Review Queue', path: '/dashboard/review-queue' },
        { text: 'Analytics', path: '/dashboard/analytics' },
        // Utilization is admin-only now; it becomes a firm-level feature later.
        ...(isAdmin ? [{ text: 'Utilization', path: '/dashboard/utilization' }] : []),
        // Superadmin-only: global partner integrations (e.g. HealthSpan Wealth).
        ...(role === 'superadmin'
          ? [{ text: 'Partner Integrations', path: '/dashboard/partner-integrations' }]
          : []),
      ]
    : [];

  const closeAllMenus = () => {
    setProfileAnchor(null);
    setAdminAnchor(null);
    setMobileAnchor(null);
  };

  const goTo = (path: string) => {
    closeAllMenus();
    navigate(path);
  };

  const handleLogout = async () => {
    closeAllMenus();
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const isActive = (path: string) =>
    path === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname.startsWith(path);

  const isAdminActive = adminItems.some((i) => isActive(i.path));

  // Prefer the dashboard user record's name (users collection — editable in User
  // Management) over the Auth0 token claims, which are frozen at account creation.
  const userDisplayName =
    dbUser?.name || user?.name || user?.given_name || user?.email?.split('@')[0] || 'User';

  const navButtonSx = (active: boolean) => ({
    px: 1.75,
    py: 0.9,
    borderRadius: '8px',
    fontSize: 13.5,
    fontWeight: active ? 600 : 500,
    color: active ? tokens.navy : tokens.textSec,
    bgcolor: active ? tokens.lightBlue : 'transparent',
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
    '&:hover': {
      bgcolor: active ? tokens.lightBlue : tokens.bg,
      color: tokens.navy,
    },
  });

  const showLogo = dbUser?.showPlanScoutLogo !== false;

  // Publish the sticky chrome's height (top bar + view-as banner) as a CSS
  // variable so pages with sticky table headers can offset them exactly, even
  // when the bar wraps or the banner appears/disappears.
  const stickyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return;
    const publish = () =>
      document.documentElement.style.setProperty('--ps-topnav-h', `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.bg }}>
      {/* ── Sticky chrome: top bar + (optional) enterprise view-as banner ── */}
      <Box ref={stickyRef} sx={{ position: 'sticky', top: 0, zIndex: (t) => t.zIndex.appBar }}>
      {/* ── Top bar ── */}
      <Box
        sx={{
          bgcolor: tokens.card,
          borderBottom: `1px solid ${tokens.border}`,
        }}
      >
        <Box
          sx={{
            maxWidth: 1280,
            mx: 'auto',
            px: { xs: 2, md: 3.5 },
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 3 },
          }}
        >
          {/* Logo / Home */}
          {showLogo ? (
            <Box
              component="button"
              onClick={() => goTo('/dashboard')}
              aria-label="Home"
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                p: 0,
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={planscoutLogo}
                alt="PlanScout"
                sx={{ height: 32, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Button onClick={() => goTo('/dashboard')} sx={navButtonSx(isActive('/dashboard'))}>
              Home
            </Button>
          )}

          {/* Center nav (desktop) */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5, flex: 1, alignItems: 'center' }}>
              {NAV_ITEMS.map((item) => (
                <Button key={item.path} onClick={() => goTo(item.path)} sx={navButtonSx(isActive(item.path))}>
                  {item.text}
                </Button>
              ))}
              {adminItems.length > 0 && (
                <Button
                  onClick={(e) => setAdminAnchor(e.currentTarget)}
                  endIcon={<ArrowDownIcon sx={{ fontSize: 16 }} />}
                  sx={navButtonSx(isAdminActive)}
                >
                  Admin
                </Button>
              )}
            </Box>
          )}

          {isMobile && <Box sx={{ flex: 1 }} />}

          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton onClick={(e) => setMobileAnchor(e.currentTarget)} aria-label="Menu">
              <MenuIcon />
            </IconButton>
          )}

          {/* User menu */}
          <Box
            component="button"
            onClick={(e: React.MouseEvent<HTMLElement>) => setProfileAnchor(e.currentTarget)}
            aria-label="Account menu"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0.5,
              borderRadius: '8px',
              flexShrink: 0,
              '&:hover': { bgcolor: tokens.bg },
            }}
          >
            {!isMobile && (
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: tokens.text }}>
                {userDisplayName}
              </Typography>
            )}
            {/* Advisor headshot when one is uploaded; MUI falls back to the
                initial letter if the src is missing or fails to load. */}
            <Avatar
              src={advisorPhotoUrl ?? undefined}
              alt={userDisplayName}
              sx={{ width: 30, height: 30, bgcolor: tokens.navy, fontSize: 13 }}
            >
              {dbUser?.name || user?.name ? (
                (dbUser?.name || user!.name!).charAt(0).toUpperCase()
              ) : user?.email ? (
                user.email.charAt(0).toUpperCase()
              ) : (
                <AccountCircleIcon />
              )}
            </Avatar>
          </Box>
        </Box>
      </Box>

      {/* ── Menus ── */}
      <Menu
        anchorEl={adminAnchor}
        open={Boolean(adminAnchor)}
        onClose={() => setAdminAnchor(null)}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {adminItems.map((item) => (
          <MenuItem key={item.path} selected={isActive(item.path)} onClick={() => goTo(item.path)}>
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile combined nav menu */}
      <Menu
        anchorEl={mobileAnchor}
        open={Boolean(mobileAnchor)}
        onClose={() => setMobileAnchor(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <MenuItem selected={location.pathname === '/dashboard'} onClick={() => goTo('/dashboard')}>
          Home
        </MenuItem>
        {NAV_ITEMS.concat(MORE_ITEMS).map((item) => (
          <MenuItem key={item.path} selected={isActive(item.path)} onClick={() => goTo(item.path)}>
            {item.text}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            closeAllMenus();
            setNewPlanModalOpen(true);
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          Add Client
        </MenuItem>
        {adminItems.length > 0 && <Divider />}
        {adminItems.map((item) => (
          <MenuItem key={item.path} selected={isActive(item.path)} onClick={() => goTo(item.path)}>
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {dbUser?.name || user?.name || user?.email || 'User'}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeAllMenus();
            setPreferencesModalOpen(true);
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Preferences
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* ── Enterprise "view as advisor" banner ── */}
      {viewAsCustomerId && (
        <Box
          sx={{
            bgcolor: tokens.navy,
            color: '#fff',
            px: { xs: 2, md: 3.5 },
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            fontSize: 13,
          }}
        >
          <Box component="span">
            Viewing as{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {viewAsAdvisorName || `Advisor ${viewAsCustomerId}`}
            </Box>{' '}
            — you're seeing their advisor view.
          </Box>
          <Box
            component="button"
            onClick={() => {
              exitViewAs();
              navigate('/dashboard/enterprise');
            }}
            sx={{
              background: 'rgba(255,255,255,0.14)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '6px',
              px: 1.25,
              py: 0.25,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:hover': { background: 'rgba(255,255,255,0.24)' },
            }}
          >
            Back to enterprise
          </Box>
        </Box>
      )}
      </Box>

      {/* ── Main content ── */}
      {/* Admin data tables (Customer Management) are denser than the 1280px
          design width — give that route room so action buttons don't clip. */}
      <Box
        component="main"
        sx={{
          maxWidth: location.pathname.startsWith('/dashboard/customer-management') ? 1720 : 1280,
          mx: 'auto',
          px: { xs: 2, md: 3.5 },
          py: 4,
          pb: 10,
        }}
      >
        <Outlet />
      </Box>

      <NewPlanRequestModal
        open={newPlanModalOpen}
        onClose={() => setNewPlanModalOpen(false)}
        onSuccess={() => {
          console.log('Plan request created successfully');
        }}
      />

      <PreferencesModal open={preferencesModalOpen} onClose={() => setPreferencesModalOpen(false)} />
    </Box>
  );
};

export default TopNavLayout;
